// src/components/TestConsole.tsx

import { useState } from 'react';
import apiService from '../services/api';

type EndpointType = 'location' | 'security' | 'network' | 'reputation' | 'full';

function TestConsole() {
  const [ipAddress, setIpAddress] = useState('8.8.8.8');
  const [endpointType, setEndpointType] = useState<EndpointType>('full');
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [responseTime, setResponseTime] = useState<number | null>(null);

  const handleTest = async () => {
    if (!ipAddress.trim()) {
      setError('Please enter an IP address');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);
    setResponseTime(null);

    const startTime = performance.now();

    try {
      const data = await apiService.lookupIp(ipAddress, endpointType);
      const endTime = performance.now();
      setResponseTime(Math.round(endTime - startTime));
      setResult(data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to lookup IP');
      console.error('Lookup error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGetMyIp = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiService.getMyIp();
      setIpAddress(data.ip);
    } catch (err: any) {
      setError('Failed to get your IP address');
      console.error('MyIP error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTest();
    }
  };

  return (
    <div>
      <div className="card">
        <h2>IP Lookup Test Console</h2>
        
        <div className="test-input-group">
          <input
            className="input"
            type="text"
            value={ipAddress}
            onChange={(e) => setIpAddress(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Enter IP address (e.g., 8.8.8.8)"
            disabled={loading}
          />
          <button 
            className="btn btn-secondary"
            onClick={handleGetMyIp} 
            disabled={loading}
          >
            Use My IP
          </button>
        </div>

        <div className="test-input-group">
          <select
            className="input select-input"
            value={endpointType}
            onChange={(e) => setEndpointType(e.target.value as EndpointType)}
            disabled={loading}
          >
            <option value="location">Location (Geolocation)</option>
            <option value="security">Security (VPN/Proxy/TOR)</option>
            <option value="network">Network (ISP/ASN)</option>
            <option value="reputation">Reputation (Threat/Abuse)</option>
            <option value="full">Full (All Data)</option>
          </select>
        </div>

        <div className="test-actions">
          <button 
            className="btn btn-primary"
            onClick={handleTest} 
            disabled={loading}
          >
            {loading ? 'Testing...' : 'Test Lookup'}
          </button>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {responseTime !== null && (
          <div className="success-message">
            Response Time: <strong>{responseTime}ms</strong>
          </div>
        )}
      </div>

      {result && (
        <div className="card">
          <div className="card-header">
            <h2>Results for {result.ip || ipAddress}</h2>
          </div>
          
          {result.metadata && (
            <div className="result-section">
              <p>
                <strong>Status:</strong> {result.metadata.cached ? '🔄 Cached' : '✨ Fresh'} | 
                <strong> Timestamp:</strong> {new Date(result.metadata.timestamp).toLocaleString()}
              </p>
            </div>
          )}

          <div className="result-section">
            <pre style={{
              background: '#2d2d2d',
              color: '#f8f8f2',
              padding: '1rem',
              borderRadius: '6px',
              overflow: 'auto',
              fontSize: '0.875rem',
              lineHeight: '1.5'
            }}>
              {JSON.stringify(result, null, 2)}
            </pre>
          </div>
        </div>
      )}

      <div className="card">
        <h2>Quick Test Examples</h2>
        <div className="test-actions">
          <button 
            className="btn btn-secondary"
            onClick={() => { setIpAddress('8.8.8.8'); setEndpointType('location'); }}
          >
            Google DNS (Location)
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => { setIpAddress('1.1.1.1'); setEndpointType('security'); }}
          >
            Cloudflare (Security)
          </button>
          <button 
            className="btn btn-secondary"
            onClick={() => { setIpAddress('104.200.20.46'); setEndpointType('full'); }}
          >
            Random IP (Full)
          </button>
        </div>
      </div>
    </div>
  );
}

export default TestConsole;